/**
 * Mobile Menu Slider Component
 * Two-layer sliding navigation for mobile menu
 */

if (!customElements.get('mobile-menu-slider')) {
  customElements.define(
    'mobile-menu-slider',
    class MobileMenuSlider extends HTMLElement {
      constructor() {
        super();

        this.panels = [];
        this.currentPanelIndex = 0;
        this.isAnimating = false;
        this.isNavigating = false;
        this.initialized = false;
        this.handledByTouch = false;

        // Bind methods
        this.handleBackClick = this.handleBackClick.bind(this);
        this.handleMenuItemClick = this.handleMenuItemClick.bind(this);
        this.handleClick = this.handleClick.bind(this);
      }

      connectedCallback() {
        // Prevent multiple initializations
        if (this.initialized) return;
        
        this.init();

        // Find the drawer footer
        this.drawerFooter = document.querySelector('.drawer__foot');

        this.drawer = this.closest('drawer-element');
        if (this.drawer) {
          this.observeDrawer();
        }
      }

      observeDrawer() {
        this.drawerWasOpen = this.drawer.classList.contains('is-open');

        this.drawerObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (
              mutation.type === 'attributes' &&
              mutation.attributeName === 'class'
            ) {
              const isOpen = this.drawer.classList.contains('is-open');

              // only reset when drawer transitions from closed to open
              if (isOpen && !this.drawerWasOpen) {
                document.querySelector('.mobile-menu-tabs__tab[data-menu-tab="menu-shop"]')?.click();
                this.resetToMain();
              }

              this.drawerWasOpen = isOpen;
            }
          });
        });

        this.drawerObserver.observe(this.drawer, {
          attributes: true,
          attributeFilter: ['class']
        });
      }


      init() {
        // Mark as initialized
        this.initialized = true;
        
        // Create panels container
        this.panelsContainer = document.createElement('div');
        this.panelsContainer.className = 'mobile-menu-slider__panels';

        // Get the main menu list
        const mainMenuList = this.querySelector('.mobile-nav');
        if (!mainMenuList) return;

        // Create first (main) panel
        const mainPanel = this.createPanel(mainMenuList, 'Main Menu', null, true);
        this.panels.push(mainPanel);
        this.panelsContainer.appendChild(mainPanel);

        // Replace original content with panels container
        this.innerHTML = '';
        this.appendChild(this.panelsContainer);

        // Initialize event listeners
        this.initEventListeners();

        // Show first panel immediately
        requestAnimationFrame(() => {
          this.panels[0].classList.add('is-active');
        });
      }

      createPanel(content, title, parentIndex = null, isMain = false) {
        const panel = document.createElement('div');
        panel.className = 'mobile-menu-slider__panel';
        panel.dataset.panelIndex = this.panels.length;

        if (parentIndex !== null) {
          panel.dataset.parentIndex = parentIndex;
        }

        // Create panel header with back button
        if (!isMain) {
          const header = document.createElement('div');
          header.className = 'mobile-menu-slider__header';

          const backButton = document.createElement('button');
          backButton.type = 'button';
          backButton.className = 'mobile-menu-slider__back';
          backButton.innerHTML = `
            <svg width="15" height="10" viewBox="0 0 15 10" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5.87221 12.4557C5.98712 12.5935 6.15475 12.6767 6.33411 12.6848C6.5134 12.6929 6.68784 12.6252 6.81475 12.4983C6.94173 12.3713 7.00941 12.1969 7.00126 12.0176C6.99318 11.8383 6.91003 11.6706 6.77221 11.5557L2.18042 6.95846L17.3574 6.95846C17.578 6.9468 17.7769 6.82234 17.884 6.6291C17.991 6.43592 17.991 6.20126 17.884 6.00801C17.7769 5.81483 17.578 5.69037 17.3574 5.67866L2.18042 5.67866L6.77942 1.08686C6.8952 0.965626 6.95976 0.804436 6.95976 0.636856C6.95976 0.469216 6.89519 0.308086 6.77942 0.186856C6.66024 0.0672655 6.49829 1.85864e-06 6.32942 1.84387e-06C6.16055 1.82911e-06 5.99865 0.0672673 5.87942 0.186856L0.191414 5.87486C0.0691305 5.99257 -1.46879e-05 6.15506 -1.47027e-05 6.32486C-1.47176e-05 6.49466 0.0691267 6.65708 0.191414 6.77486L5.87221 12.4557Z" fill="black"/>
            </svg>
            <span>${title}</span>
          `;
          backButton.addEventListener('click', this.handleBackClick);

          header.appendChild(backButton);
          panel.appendChild(header);
        }

        // Create panel body
        const body = document.createElement('div');
        body.className = 'mobile-menu-slider__body';

        if (typeof content === 'string') {
          body.innerHTML = content;
        } else {
          body.appendChild(content.cloneNode(true));
        }

        panel.appendChild(body);

        return panel;
      }

      initEventListeners() {
        // Listen for clicks on menu items with children
        // Use { passive: false } to allow preventDefault on touch devices
        this.addEventListener('click', this.handleClick);
        
        // Prevent ghost clicks on touch devices by tracking touch events
        this.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: false });
      }
      
      handleTouchEnd(e) {
        const navlink = e.target.closest('.mobile-navlink');
        if (!navlink) return;
        
        const listItem = navlink.parentElement?.closest('.mobile-menu__item');
        if (!listItem) return;
        
        // If this is a menu item with children, prevent the subsequent click event
        if (listItem.classList.contains('mobile-menu__item--has-items')) {
          const directNavlink = listItem.querySelector(':scope > .mobile-navlink');
          if (directNavlink === navlink) {
            // Mark that we handled this via touch
            this.handledByTouch = true;
            // Handle the navigation
            this.handleMenuItemClick(e, navlink, listItem);
            
            // Reset the flag after a short delay (after click event would fire)
            setTimeout(() => {
              this.handledByTouch = false;
            }, 300);
          }
        }
      }

      handleClick(e) {
        // Skip if already handled by touch event
        if (this.handledByTouch) return;
        
        // Ignore if this slider is hidden
        if (this.classList.contains('mobile-menu-slider--hidden')) return;
        
        // Check if the click is on a navlink with children
        const navlink = e.target.closest('.mobile-navlink');
        if (!navlink) return;
        
        // Make sure the navlink is within THIS slider instance
        if (!this.contains(navlink)) return;

        // Get the direct parent list item of this navlink
        const listItem = navlink.parentElement.closest('.mobile-menu__item');
        if (!listItem) return;

        // Only handle if this list item has children AND the navlink is the direct trigger
        if (listItem.classList.contains('mobile-menu__item--has-items')) {
          // Make sure the navlink is the direct child link of this list item
          const directNavlink = listItem.querySelector(':scope > .mobile-navlink');
          if (directNavlink === navlink) {
            this.handleMenuItemClick(e, navlink, listItem);
          }
        }
      }

      handleMenuItemClick(e, navlink, listItem) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        // Block if already processing a navigation
        if (this.isAnimating || this.isNavigating) return;
        this.isNavigating = true;

        // Find the submenu within this list item
        const sublist = listItem.querySelector(':scope > .mobile-dropdown > .mobile-nav__sublist');

        if (!sublist) {
          this.isNavigating = false;
          return;
        }

        const title = navlink.textContent.trim();
        const currentPanelIndex = this.currentPanelIndex;

        // Flatten all submenu items into a single list
        const flattenedContent = this.flattenSubmenu(sublist);

        // Create new panel with flattened submenu content
        const newPanel = this.createPanel(flattenedContent, title, currentPanelIndex);
        this.panels.push(newPanel);
        this.panelsContainer.appendChild(newPanel);

        // Slide to new panel
        this.showPanel(this.panels.length - 1);
        
        // Reset navigation lock after animation completes
        setTimeout(() => {
          this.isNavigating = false;
        }, 350);

        // Hide footer when navigating deeper
        if (this.drawerFooter) {
          this.drawerFooter.style.display = 'none';
        }

        // Dispatch event with parent menu title for promo banner filtering
        this.dispatchEvent(new CustomEvent('mobile-menu:navigate', {
          bubbles: true,
          detail: {
            parentTitle: title,
            menuId: this.dataset.menuId,
            blockId: this.dataset.blockId
          }
        }));
      }

      flattenSubmenu(sublist) {
        const flatList = document.createElement('ul');
        flatList.className = 'mobile-nav';

        // Get all submenu items (level 2 and level 3)
        const items = sublist.querySelectorAll('.mobile-menu__item');

        items.forEach((item) => {
          // Check if this is a level-2 item with children (has sub-sublist)
          const hasSubItems = item.classList.contains('mobile-menu__item--has-items');
          const level2Link = item.querySelector(':scope > .mobile-navlink');

          if (hasSubItems) {
            // This is a category header - add it as a header/divider
            const headerItem = document.createElement('li');
            headerItem.className = 'mobile-menu__item mobile-menu__item--header';
            headerItem.innerHTML = `<span class="mobile-navlink mobile-navlink--header">${level2Link.textContent.trim()}</span>`;
            flatList.appendChild(headerItem);

            // Get all the sub-sub items and add them
            const subSubList = item.querySelector('.mobile-nav__sub-sublist');
            if (subSubList) {
              const subSubItems = subSubList.querySelectorAll(':scope > .mobile-menu__item');
              subSubItems.forEach((subSubItem) => {
                const clonedItem = subSubItem.cloneNode(true);
                clonedItem.classList.remove('mobile-menu__item--level-3');
                clonedItem.classList.add('mobile-menu__item--level-2');
                flatList.appendChild(clonedItem);
              });
            }
          } else {
            // This is a simple level-2 item without children - just clone it
            const clonedItem = item.cloneNode(true);
            flatList.appendChild(clonedItem);
          }
        });

        return flatList;
      }

      handleBackClick(e) {
        e.preventDefault();

        if (this.isAnimating) return;

        const currentPanel = this.panels[this.currentPanelIndex];
        const parentIndex = parseInt(currentPanel.dataset.parentIndex);

        if (!isNaN(parentIndex)) {
          this.showPanel(parentIndex);

          // Show footer if going back to main panel (index 0)
          if (parentIndex === 0 && this.drawerFooter) {
            this.drawerFooter.style.display = '';
          }

          // Dispatch event for going back to main menu
          if (parentIndex === 0) {
            this.dispatchEvent(new CustomEvent('mobile-menu:navigate', {
              bubbles: true,
              detail: {
                parentTitle: null,
                menuId: this.dataset.menuId,
                blockId: this.dataset.blockId
              }
            }));
          }

          // Remove the current panel after animation
          setTimeout(() => {
            currentPanel.remove();
            this.panels.splice(this.currentPanelIndex, 1);
          }, 300);
        }
      }

      showPanel(index) {
        if (this.isAnimating || index < 0 || index >= this.panels.length) return;

        this.isAnimating = true;

        const direction = index > this.currentPanelIndex ? 'forward' : 'backward';
        const previousPanel = this.panels[this.currentPanelIndex];
        const nextPanel = this.panels[index];

        // Set the container height to the current panel's height before transitioning
        const currentHeight = previousPanel.offsetHeight;
        this.panelsContainer.style.height = currentHeight + 'px';

        // Measure the next panel's height while keeping it absolutely positioned at top
        nextPanel.style.position = 'absolute';
        nextPanel.style.top = '0';
        nextPanel.style.left = '0';
        nextPanel.style.visibility = 'hidden';
        nextPanel.style.opacity = '0';
        nextPanel.style.transform = 'none';
        const nextHeight = nextPanel.offsetHeight;
        // Don't reset position - keep it absolute during animation
        nextPanel.style.visibility = '';
        nextPanel.style.opacity = '';
        nextPanel.style.transform = '';

        // Set up animation classes
        previousPanel.classList.add('is-active');
        nextPanel.classList.add('is-next');

        requestAnimationFrame(() => {
          // Animate the container height
          this.panelsContainer.style.transition = 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
          this.panelsContainer.style.height = nextHeight + 'px';

          if (direction === 'forward') {
            previousPanel.classList.add('slide-out-left');
            nextPanel.classList.remove('is-next');
            nextPanel.classList.add('is-active', 'slide-in-right');
          } else {
            previousPanel.classList.add('slide-out-right');
            nextPanel.classList.remove('is-next');
            nextPanel.classList.add('is-active', 'slide-in-left');
          }

          // Clean up after animation
          setTimeout(() => {
            previousPanel.classList.remove('is-active', 'slide-out-left', 'slide-out-right');
            nextPanel.classList.remove('slide-in-left', 'slide-in-right');

            // Now reset the inline position styles to let CSS take over
            nextPanel.style.position = '';
            nextPanel.style.top = '';
            nextPanel.style.left = '';

            this.currentPanelIndex = index;
            this.isAnimating = false;

            // Remove fixed height and transition after animation completes
            this.panelsContainer.style.height = 'auto';
            this.panelsContainer.style.transition = '';
          }, 300);
        });
      }

      resetToMain() {
        // Reset to the main panel (index 0)
        if (this.currentPanelIndex === 0) return;

        // Remove all panels except the first one
        while (this.panels.length > 1) {
          const panel = this.panels.pop();
          panel.remove();
        }

        // Show the main panel
        this.panels[0].classList.add('is-active');
        this.currentPanelIndex = 0;
        this.isAnimating = false;

        // Show footer when resetting to main
        if (this.drawerFooter) {
          this.drawerFooter.style.display = '';
        }
      }

      disconnectedCallback() {
        // Clean up event listeners
        this.removeEventListener('click', this.handleClick);

        if (this.drawerObserver) {
          this.drawerObserver.disconnect();
        }
      }
    }
  );
}
